import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { PUBLIC_TESTIMONIALS } from "config/api-const";
import useMediaQuery from "hooks/use-media-query";
import "./success-stories-section.css";

const SuccessStoriesSection = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const navigate = useNavigate();
  const { isDesktop, isTablet } = useMediaQuery();

  const visibleCards = isDesktop ? 3 : isTablet ? 2 : 1;

  useEffect(() => {
    const fetchStories = async () => {
      try {
        const res = await axios.get(PUBLIC_TESTIMONIALS);
        if (res.data.success) {
          setStories((res.data.testimonials || []).slice(0, 10));
        }
      } catch (err) {
        console.error("Error fetching latest success stories:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchStories();
  }, []);

  useEffect(() => {
    if (stories.length <= visibleCards) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => {
        const maxIndex = stories.length - visibleCards;
        return prev >= maxIndex ? 0 : prev + 1;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [stories.length, visibleCards]);

  if (loading) {
    return (
      <section className="landing-success-stories">
        <div className="section-header">
          <h2>Successful Matches</h2>
          <h6 className="section-subtitle">Happily Ever Afters</h6>
        </div>
        <div className="stories-loading">
          <div className="spinner"></div>
        </div>
      </section>
    );
  }

  return (
    <section className="landing-success-stories">
      <div className="section-header">
        <h2>Successful Matches</h2>
        <h6 className="section-subtitle">
          Real couples, real love stories on Kalyanawaran
        </h6>
      </div>

      {stories.length === 0 ? (
        <div className="no-stories-yet">
          <p>No success stories posted yet. Be the first to share yours!</p>
          <button
            className="btn btn-outline-danger btn-rounded"
            onClick={() => navigate("/testimonial")}
          >
            Post Your Testimonial
          </button>
        </div>
      ) : (
        <>
          <div className="carousel-container">
            {stories.length > visibleCards && (
              <button
                className="carousel-btn prev"
                onClick={() =>
                  setCurrentIndex((prev) =>
                    prev === 0 ? stories.length - visibleCards : prev - 1
                  )
                }
                aria-label="Previous story"
              >
                ‹
              </button>
            )}

            <div className="carousel-track-wrapper">
              <div
                className="carousel-track"
                style={{
                  transform: `translate3d(-${currentIndex * (100 / visibleCards)}%, 0, 0)`,
                  transition:
                    "transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                }}
              >
                {stories.map((story) => (
                  <div
                    key={story._id || story.timestamp}
                    className="landing-story-card-wrapper"
                    style={{
                      flex: `0 0 ${100 / visibleCards}%`,
                    }}
                  >
                    <div
                      className="landing-story-card"
                      onClick={() => navigate("/success-stories")}
                    >
                      {/* Photos */}
                      <div className="landing-photos-container">
                        {story.photo1 && story.photo2 ? (
                          <>
                            <div className="landing-photo-wrapper bride">
                              <img
                                src={story.photo1}
                                alt={story.bName || "Bride"}
                                loading="lazy"
                              />
                            </div>
                            <div className="landing-heart-connector">
                              <span className="heart-icon">♥</span>
                            </div>
                            <div className="landing-photo-wrapper groom">
                              <img
                                src={story.photo2}
                                alt={story.gName || "Groom"}
                                loading="lazy"
                              />
                            </div>
                          </>
                        ) : (
                          <div className="landing-photo-wrapper single">
                            <img
                              src={story.photo1 || story.photo2}
                              alt="Wedding photo"
                              loading="lazy"
                            />
                          </div>
                        )}
                      </div>

                      {/* Couple names */}
                      <div className="landing-couple-names">
                        {story.bName && story.gName ? (
                          <p className="landing-names-text">
                            <span className="landing-bride-name">
                              {story.bName}
                            </span>
                            <span className="landing-names-heart"> ♥ </span>
                            <span className="landing-groom-name">
                              {story.gName}
                            </span>
                          </p>
                        ) : (
                          <p className="landing-names-text">
                            {story.bName || story.gName || "Happy Couple"}
                          </p>
                        )}
                        {(story.bCity || story.gCity) && (
                          <p className="landing-city-text">
                            📍{" "}
                            {[story.bCity, story.gCity]
                              .filter(Boolean)
                              .join(" & ")}
                          </p>
                        )}
                      </div>

                      {/* Stars */}
                      <div className="landing-rating-container">
                        <div className="landing-stars">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <span
                              key={i}
                              className={`landing-star ${i < story.rating ? "lit" : ""}`}
                            >
                              ★
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {stories.length > visibleCards && (
              <button
                className="carousel-btn next"
                onClick={() =>
                  setCurrentIndex((prev) => {
                    const maxIndex = stories.length - visibleCards;
                    return prev >= maxIndex ? 0 : prev + 1;
                  })
                }
                aria-label="Next story"
              >
                ›
              </button>
            )}
          </div>

          {stories.length > visibleCards && (
            <div className="carousel-dots">
              {Array.from({ length: stories.length - visibleCards + 1 }).map(
                (_, i) => (
                  <span
                    key={i}
                    className={`carousel-dot ${currentIndex === i ? "active" : ""}`}
                    onClick={() => setCurrentIndex(i)}
                  />
                )
              )}
            </div>
          )}

          <div className="view-all-container">
            <button
              className="btn btn-outline-danger btn-rounded view-all-btn"
              onClick={() => navigate("/success-stories")}
            >
              View All Success Stories →
            </button>
          </div>
        </>
      )}
    </section>
  );
};

export default SuccessStoriesSection;
